import React, { useState, useEffect, useCallback } from 'react';
import { FaChevronDown, FaChevronRight, FaBox, FaPaperPlane, FaCalendarAlt, FaTrash } from 'react-icons/fa';
import './css_pages/ViewOrders.css';
import { useStore } from '../contexts/StoreContext';
import api from '../services/api'; // ajusta al path de tu instancia axios

// ============================================================
// HELPER — fecha a YYYY-MM-DD sin timezone
// ============================================================
const toDateString = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val.toISOString().split('T')[0];
  return String(val).split('T')[0];
};

// ============================================================
// SERVICES
// ============================================================
const getOrderDates = (storeId, filterType) =>
  api.get('/orders/view/dates', { params: { storeId, filterType } }).then(r => r.data);

const getOrdersForView = (storeId, filterType, filterDate) =>
  api.get('/orders/view', { params: { storeId, filterType, filterDate } }).then(r => r.data);

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
function ViewOrders() {
  const { selectedStore } = useStore();

  const [filterType, setFilterType]         = useState('sent_date');
  const [filterDate, setFilterDate]         = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [orders, setOrders]                 = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  // ============================================================
  // FETCH FECHAS DISPONIBLES
  // ============================================================
  const fetchDates = useCallback(async () => {
    if (!selectedStore) return;
    try {
      const response = await getOrderDates(selectedStore.id, filterType);
      if (response.success) {
        setAvailableDates(response.data);
        setFilterDate('');
        setOrders([]);
      }
    } catch {
      setAvailableDates([]);
    }
  }, [selectedStore, filterType]);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  // ============================================================
  // FETCH ÓRDENES
  // ============================================================
  const fetchOrders = useCallback(async () => {
    if (!selectedStore || !filterDate) return;
    try {
      setLoading(true);
      setError(null);
      // Truncar fecha por si viene con timezone
      const cleanDate = toDateString(filterDate);
      const response = await getOrdersForView(selectedStore.id, filterType, cleanDate);
      if (response.success) {
        setOrders(response.orders);
        setExpandedOrders({});
      }
    } catch {
      setError('Error loading orders');
    } finally {
      setLoading(false);
    }
  }, [selectedStore, filterType, filterDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ============================================================
  // TOGGLE EXPAND
  // ============================================================
  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const expandAll = () => {
    const allExpanded = {};
    orders.forEach(o => { allExpanded[o.id_orders] = true; });
    setExpandedOrders(allExpanded);
  };

  const collapseAll = () => setExpandedOrders({});

  const handleDeleteOrder = async (orderId) => {
    if(!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    try{
      await api.delete(`/orders/${orderId}`);
      setOrders(prev => prev.filter(o => o.id_orders !== orderId));
    }catch{
      alert('Error deleting order');
    }
  }

  // ============================================================
  // HELPERS DE FORMATO
  // ============================================================
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-MX', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '—';
    // Agregar T00:00:00 para evitar desplazamiento de timezone
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const totalProducts = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);

  // ============================================================
  // GUARD
  // ============================================================
  if (!selectedStore) {
    return (
      <div className="vo-page">
        <div className="page-header"><h1>View Orders</h1></div>
        <div className="no-store-message">
          <p>Please select a store from the sidebar to view orders</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="vo-page">

      {/* HEADER */}
      <div className="page-header sticky-header">
        <div className="header-left">
          <h1>View Orders</h1>
          <span className="store-badge">{selectedStore.name}</span>
        </div>
      </div>

      {/* FILTROS */}
      <div className="vo-filters">
        {/* Tabs tipo de filtro */}
        <div className="vo-filter-tabs">
          <button
            className={`vo-tab ${filterType === 'sent_date' ? 'active' : ''}`}
            onClick={() => setFilterType('sent_date')}
          >
            <FaPaperPlane /> By Sent Date
          </button>
          <button
            className={`vo-tab ${filterType === 'inventory_date' ? 'active' : ''}`}
            onClick={() => setFilterType('inventory_date')}
          >
            <FaCalendarAlt /> By Inventory Date
          </button>
        </div>

        {/* Selector de fecha */}
        <div className="vo-date-selector">
          <label>
            {filterType === 'sent_date' ? 'Select sent date:' : 'Select inventory date:'}
          </label>
          <select
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="vo-select"
            disabled={availableDates.length === 0}
          >
            <option value="">-- Select a date --</option>
            {availableDates.map(d => (
              <option key={d.date} value={d.date}>
                {formatDateLong(d.date)} ({d.order_count} order{d.order_count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ESTADOS */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchOrders}>Retry</button>
        </div>
      )}

      {loading && <div className="loading">Loading orders...</div>}

      {!loading && filterDate && orders.length === 0 && (
        <div className="vo-empty">
          <FaBox style={{ fontSize: 32, color: '#cbd5e1', marginBottom: 12 }} />
          <p>No orders found for this date</p>
        </div>
      )}

      {/* RESULTADOS */}
      {!loading && orders.length > 0 && (
        <>
          {/* SUMMARY BAR */}
          <div className="vo-summary-bar">
            <div className="vo-summary-stats">
              <span className="vo-stat">
                <strong>{orders.length}</strong> order{orders.length !== 1 ? 's' : ''}
              </span>
              <span className="vo-stat-divider">·</span>
              <span className="vo-stat">
                <strong>{totalProducts}</strong> product{totalProducts !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="vo-expand-actions">
              <button className="vo-btn-text" onClick={expandAll}>Expand all</button>
              <span>·</span>
              <button className="vo-btn-text" onClick={collapseAll}>Collapse all</button>
            </div>
          </div>

          {/* LISTA DE ÓRDENES */}
          <div className="vo-orders-list">
            {orders.map((order) => {
              const isExpanded = !!expandedOrders[order.id_orders];
              return (
                <div key={order.id_orders} className={`vo-order-card ${isExpanded ? 'expanded' : ''}`}>

                  {/* HEADER — clickable */}
                  <div className="vo-order-header" onClick={() => toggleExpand(order.id_orders)}>
                    <div className="vo-order-chevron">
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </div>

                    <div className="vo-order-meta">
                      <span className="vo-order-number">{order.order_number}</span>
                      <span className="vo-order-vendor">{order.vendor_name}</span>
                    </div>

                    <div className="vo-order-dates">
                      <span className="vo-order-date-label">Sent:</span>
                      <span className="vo-order-date-value">{formatDateTime(order.sent_at)}</span>
                      {order.inventory_date && (
                        <>
                          <span className="vo-order-date-label">Inventory:</span>
                          <span className="vo-order-date-value">
                            {formatDateLong(order.inventory_date)}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="vo-order-badge">
                      <span className="vo-items-count">
                        {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <button className='vo-btn-delete' onClick={(e) => {e.stopPropagation(); handleDeleteOrder(order.id_orders);}} title='Delete order'>
                      <FaTrash/>
                    </button>
                  </div>

                  {/* TABLA DE PRODUCTOS — expandible */}
                  {isExpanded && (
                    <div className="vo-order-body">
                      <table className="vo-products-table">
                        <thead>
                          <tr>
                            <th>Product Code</th>
                            <th>Product Name</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items && order.items.length > 0 ? (
                            order.items.map((item, idx) => (
                              <tr key={`${order.id_orders}-${idx}`}>
                                <td className="vo-cell-code">{item.product_code || '—'}</td>
                                <td className="vo-cell-name">{item.product_name}</td>
                                <td className="vo-cell-qty">{item.quantity}</td>
                                <td className="vo-cell-unit">{item.unit || '—'}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                                No items in this order
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loading && !filterDate && (
        <div className="vo-prompt">
          <FaCalendarAlt style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 16 }} />
          <p>Select a date above to view orders</p>
        </div>
      )}

    </div>
  );
}

export default ViewOrders;